// Only rock and shut doors stand in the party's way. Everything else can be
// stepped onto, including whatever they might then want to use: stairs,
// chests, the trainer's desk and the town's shopfronts. Keeping it a rule
// rather than a list means a new kind of square is walkable by default.
const BLOCKING_TILES = [1, 2, 3, 5];

export function canStepOnto(tile) {
  return tile !== undefined && !BLOCKING_TILES.includes(tile);
}

// Camera position for the party: set back half a tile from the tile centre,
// opposite the direction the party is facing
export function coordsToPosition(coords, heading){
  const gridModifier = {
    '0':[0,0.5],
    '90':[0.5,0],
    '180':[0,-0.5],
    '270':[-.5,0],
}
  return [coords[0] + gridModifier[heading][0],0,coords[1]+ gridModifier[heading][1]];
}

// A spell effect that lands on one of the party is drawn over their slot in
// the line, whatever spell it was; everything else flies at a foe and needs a
// target to aim at. Keyed on the effect rather than on one spell's name, so a
// new healing spell is drawn properly without being listed anywhere.
export function healsAnAlly(effect) {
  return !!effect && effect.allyIndex !== undefined && effect.allyIndex !== null;
}

// How long each effect plays for, in seconds (missiles, then spells by id)
export const EFFECT_DURATIONS = {
  missile: 0.45,
  magicArrow: 0.55,
  energyBlast: 0.9,
  flyingFist: 0.65,
  firstAid: 1.0,
  lightningBolt: 0.7,
  prayerOfHealing: 1.0,
};

// Unit grid vectors for looking along a heading: `forward` is the direction
// of travel, `right` is to the party's right. Both as [x, z] world offsets.
const HEADING_FORWARD = { 0: [0, -1], 90: [-1, 0], 180: [0, 1], 270: [1, 0] };

export function headingAxes(heading) {
  const [fx, fz] = HEADING_FORWARD[heading];
  return { forward: [fx, fz], right: [-fz, fx] };
}

// Enemies fighting the party stand on the party's own square, lined up in
// front of the camera so they are all in view whichever way the party faces.
// Rows hold up to 3 and shrink to fit the square as they fill; the back row
// sits in the gaps of the front row. Short monsters stand at the front so the
// tall ones behind them can still be seen.
const ROW_SIZE = 3;
// Per number of foes in a row: model scale and spacing between them
const ROW_FIT = { 1: { scale: 1, spacing: 0 }, 2: { scale: 0.8, spacing: 0.42 }, 3: { scale: 0.62, spacing: 0.32 } };
// Distance ahead of the tile centre for each row (the camera sits 0.5 behind
// it, and the square ends 0.5 ahead)
const ROW_DEPTHS = [0.35, 0.47];
const MODEL_HEIGHT = { rat: 0.2, goblin: 0.6, skeleton: 0.8, orc: 0.85 };

export function combatFoeLayout(monsters, combat, coords, heading) {
  const layout = {};
  if (!combat) return layout;
  const foes = combat.monsterIds
    .map((id) => monsters.find((m) => m.id === id && m.hp > 0))
    .filter(Boolean)
    // Stable sort: shortest first, otherwise in the order they joined
    .map((monster, order) => ({ monster, order }))
    .sort((a, b) => (MODEL_HEIGHT[a.monster.model] || 0.6) - (MODEL_HEIGHT[b.monster.model] || 0.6) || a.order - b.order)
    .map(({ monster }) => monster);
  const { forward: [fx, fz], right: [rx, rz] } = headingAxes(heading);
  // Face back toward the camera
  const facing = Math.atan2(-fx, -fz);
  const frontCount = Math.min(ROW_SIZE, foes.length);

  foes.forEach((monster, index) => {
    const row = Math.floor(index / ROW_SIZE);
    const inRow = Math.min(ROW_SIZE, foes.length - row * ROW_SIZE);
    // Back rows are never drawn larger than the row in front of them
    const fit = ROW_FIT[Math.max(inRow, row > 0 ? frontCount : 1)];
    const spacing = fit.spacing || ROW_FIT[2].spacing;
    let lateral = ((index % ROW_SIZE) - (inRow - 1) / 2) * spacing;
    // Shift a back row that would line up exactly behind the front row
    if (row > 0 && inRow % 2 === frontCount % 2) lateral += ROW_FIT[frontCount].spacing / 2;
    const depth = ROW_DEPTHS[Math.min(row, ROW_DEPTHS.length - 1)];
    layout[monster.id] = {
      position: [coords[0] + fx * depth + rx * lateral, 0, coords[1] + fz * depth + rz * lateral],
      facing,
      scale: fit.scale,
    };
  });
  return layout;
}
